import axios from 'axios';
import authService from './authService';
import { API_URL } from '@/config/api';
import { registerStudyServiceWorker } from '@/pwa/studyPwa';

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});

api.interceptors.request.use(
  (config) => {
    const token = authService.token.get();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

function suportaPushNotifications() {
  return typeof window !== 'undefined'
    && 'Notification' in window
    && 'serviceWorker' in navigator
    && 'PushManager' in window;
}

function isSecureContextForServiceWorker() {
  return window.location.protocol === 'https:' || window.location.hostname === 'localhost';
}

async function garantirServiceWorkerEstudos() {
  if (!suportaPushNotifications()) {
    throw new Error('Push notification nao suportado neste navegador');
  }

  if (!isSecureContextForServiceWorker()) {
    throw new Error('Push notification exige HTTPS ou localhost');
  }

  const registration = await registerStudyServiceWorker();
  if (!registration) {
    throw new Error('Nao foi possivel registrar o service worker de estudos');
  }

  return registration;
}

async function obterSubscriptionAtual() {
  if (!suportaPushNotifications()) return null;

  const registration = await navigator.serviceWorker.getRegistration('/estudar');
  if (!registration?.pushManager) return null;

  return registration.pushManager.getSubscription();
}

async function ativarNotificacoes() {
    if (!suportaPushNotifications()) {
        throw new Error('Push notification não suportado neste navegador');
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
        throw new Error('Permissão de notificação negada');
    }

    await garantirServiceWorkerEstudos();
    const registration = await navigator.serviceWorker.ready;

    // Busca a chave pública atual do servidor
    const { data } = await api.get('/push-notifications/public-key');
    const applicationServerKey = urlBase64ToUint8Array(data.publicKey);

    const subscriptionAtual = await registration.pushManager.getSubscription();

    if (subscriptionAtual) {
        const chaveAtual = localStorage.getItem('vapidPublicKey');
        const json = subscriptionAtual.toJSON();

        if (chaveAtual === data.publicKey) {
            await api.post('/push-notifications/subscriptions', {
                endpoint: json.endpoint,
                expirationTime: json.expirationTime,
                keys: {
                    p256dh: json.keys.p256dh,
                    auth: json.keys.auth,
                },
                userAgent: navigator.userAgent,
            }).catch(console.error);

            return subscriptionAtual;
        }

        await api.post('/push-notifications/subscriptions/unsubscribe', {
            endpoint: json.endpoint,
            expirationTime: json.expirationTime,
            keys: json.keys,
            userAgent: navigator.userAgent,
        }).catch(() => {}); // ignora se já não existia no banco

        await subscriptionAtual.unsubscribe();
    }

    const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
    });

    localStorage.setItem('vapidPublicKey', data.publicKey);

    const json = subscription.toJSON();
    await api.post('/push-notifications/subscriptions', {
        endpoint: json.endpoint,
        expirationTime: json.expirationTime,
        keys: {
            p256dh: json.keys.p256dh,
            auth: json.keys.auth,
        },
        userAgent: navigator.userAgent,
    });

    return subscription;
}

export default {
  ativarNotificacoes,
  obterSubscriptionAtual,
  suportaPushNotifications,
};
